import axios from 'axios';
import { getAccessToken, clearSession } from './tokenStore';
import { refreshSession } from './session';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token (nur im Speicher, siehe
// services/tokenStore.ts -- nie aus localStorage).
api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor: on a 401, transparently refresh the access token and
// retry the request once. refreshSession() de-duplicates concurrent refreshes
// (a burst of 401s triggers a single refresh) and picks the right way for the
// deployment: Keycloak in SSO mode, the HttpOnly refresh cookie otherwise. If
// the refresh fails, clear the session and send the user to the login page
// instead of leaving them stuck on a dead 401.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await refreshSession();
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearSession();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export interface GenerateTextRequest {
  provider: 'openai' | 'claude' | 'perplexity';
  prompt: string;
  systemPrompt?: string;
  type?: string;
  maxTokens?: number;
  temperature?: number;
  // Optional: links the generated post back to the idea it was created from,
  // which also advances that idea to the "created" stage.
  postIdeaId?: string;
  // userId is now automatically taken from authentication token
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  provider: 'openai' | 'claude' | 'perplexity';
  messages: ChatMessage[];
  maxTokens?: number;
  temperature?: number;
  model?: string;
  workflowId?: string;
  // userId is now automatically taken from authentication token
}

export interface WorkflowStep {
  id: string;
  name: string;
  provider: 'openai' | 'claude' | 'perplexity';
  prompt: string;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
}

export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  steps: WorkflowStep[];
  // userId is now automatically taken from authentication token
}

export interface ExecuteWorkflowRequest {
  variables: Record<string, string>;
  // userId is now automatically taken from authentication token
}

export interface ExecuteWorkflowStepRequest extends ExecuteWorkflowRequest {
  stepIndex: number;
}

export const generationApi = {
  generateText: async (data: GenerateTextRequest) => {
    const response = await api.post('/generation/generate', data);
    return response.data;
  },

  chat: async (data: ChatRequest) => {
    const response = await api.post('/generation/chat', data);
    return response.data;
  },

  getGeneratedContent: async (limit: number = 50, offset: number = 0, workflowId?: string) => {
    const response = await api.get('/generation/content', {
      params: { limit, offset, ...(workflowId ? { workflowId } : {}) },
    });
    return response.data;
  },

  /** Ueberarbeiteten Text speichern (z. B. im Workflow per Chat angepasst). */
  updateContent: async (id: string, content: string) => {
    const response = await api.put(`/generation/content/${id}`, { content });
    return response.data;
  },

  getContentById: async (id: string) => {
    const response = await api.get(`/generation/content/${id}`);
    return response.data;
  },

  deleteContent: async (id: string) => {
    const response = await api.delete(`/generation/content/${id}`);
    return response.data;
  },
};

export const workflowApi = {
  createWorkflow: async (data: CreateWorkflowRequest) => {
    const response = await api.post('/workflows', data);
    return response.data;
  },

  getWorkflows: async () => {
    const response = await api.get('/workflows');
    return response.data;
  },

  // Creates the predefined workflow templates that are missing for the current
  // user. Idempotent, so it is safe to call from a retry path.
  seedWorkflows: async () => {
    const response = await api.post('/workflows/seed');
    return response.data as { created: number; skipped: number; total: number };
  },

  getWorkflow: async (id: string) => {
    const response = await api.get(`/workflows/${id}`);
    return response.data;
  },

  updateWorkflow: async (id: string, data: Partial<CreateWorkflowRequest> & { isActive?: boolean }) => {
    const response = await api.put(`/workflows/${id}`, data);
    return response.data;
  },

  deleteWorkflow: async (id: string) => {
    const response = await api.delete(`/workflows/${id}`);
    return response.data;
  },

  executeWorkflow: async (id: string, data: ExecuteWorkflowRequest) => {
    const response = await api.post(`/workflows/${id}/execute`, data);
    return response.data;
  },

  executeWorkflowStep: async (id: string, data: ExecuteWorkflowStepRequest) => {
    const response = await api.post(`/workflows/${id}/execute/step`, data);
    return response.data;
  },
};

export const dataApi = {
  getPresets: async () => {
    const response = await api.get('/data/presets');
    return response.data.presets as Record<string, string>;
  },
  listPresets: async () => {
    const response = await api.get('/data/presets/list');
    return response.data.presets as { key: string; filename: string; exists: boolean }[];
  },
};

export interface ScheduledPost {
  id: string;
  generatedContentId: string;
  postIdeaId?: string | null;
  contentType: 'linkedin' | 'email' | 'blog';
  scheduledDate: string;
  status: 'pending' | 'approved' | 'rejected' | 'posted';
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  postedAt?: string;
  createdAt: string;
  updatedAt: string;
  generatedContent: any;
}

export interface SchedulePostRequest {
  generatedContentId: string;
  scheduledDate: string;
  contentType?: 'linkedin' | 'email' | 'blog';
}

export const scheduledPostApi = {
  schedulePost: async (data: SchedulePostRequest) => {
    const response = await api.post('/scheduled-posts', data);
    return response.data as ScheduledPost;
  },

  getScheduledPosts: async (filters?: {
    status?: string;
    contentType?: string;
    userId?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/scheduled-posts', {
      params: filters,
    });
    return response.data as {
      posts: ScheduledPost[];
      total: number;
      limit: number;
      offset: number;
    };
  },

  getScheduledPost: async (id: string) => {
    const response = await api.get(`/scheduled-posts/${id}`);
    return response.data as ScheduledPost;
  },

  approvePost: async (id: string, approvedBy: string) => {
    const response = await api.post(`/scheduled-posts/${id}/approve`, {
      approvedBy,
    });
    return response.data as ScheduledPost;
  },

  rejectPost: async (id: string, rejectedBy: string, rejectionReason?: string) => {
    const response = await api.post(`/scheduled-posts/${id}/reject`, {
      rejectedBy,
      rejectionReason,
    });
    return response.data as ScheduledPost;
  },

  markAsPosted: async (id: string) => {
    const response = await api.post(`/scheduled-posts/${id}/mark-posted`);
    return response.data as ScheduledPost;
  },

  updateScheduledDate: async (id: string, scheduledDate: string) => {
    const response = await api.put(`/scheduled-posts/${id}/scheduled-date`, {
      scheduledDate,
    });
    return response.data as ScheduledPost;
  },

  deleteScheduledPost: async (id: string) => {
    const response = await api.delete(`/scheduled-posts/${id}`);
    return response.data;
  },
};

// Series Format API
export interface SeriesFormat {
  id: string;
  name: string;
  title: string;
  description?: string;
  footer?: string;
  schedule?: string;
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    postIdeas: number;
  };
}

export interface CreateSeriesFormatRequest {
  name: string;
  title: string;
  description?: string;
  footer?: string;
  schedule?: string;
}

export const seriesFormatApi = {
  create: async (data: CreateSeriesFormatRequest) => {
    const response = await api.post('/series-formats', data);
    return response.data as SeriesFormat;
  },

  getAll: async (includeInactive: boolean = false) => {
    const response = await api.get('/series-formats', {
      params: { includeInactive },
    });
    return response.data as SeriesFormat[];
  },

  getById: async (id: string) => {
    const response = await api.get(`/series-formats/${id}`);
    return response.data as SeriesFormat;
  },

  update: async (id: string, data: Partial<CreateSeriesFormatRequest> & { isActive?: boolean }) => {
    const response = await api.put(`/series-formats/${id}`, data);
    return response.data as SeriesFormat;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/series-formats/${id}`);
    return response.data;
  },
};

// Post Idea API
export interface PostIdea {
  id: string;
  seriesFormatId?: string;
  strategyId?: string;
  title: string;
  topic: string;
  description?: string;
  painPoint?: string;
  status: string;
  order?: number;
  userId: string;
  createdAt: string;
  updatedAt: string;
  seriesFormat?: SeriesFormat;
  strategy?: StrategyResult;
  contentBrief?: ContentBrief;
  /** Nur in der Liste: Zaehler fuer die Sprung-Chips. */
  _count?: { flyers: number; mediaAssets: number };
  linkedInPostCount?: number;
}

export interface CreatePostIdeaRequest {
  seriesFormatId?: string;
  strategyId?: string;
  title: string;
  topic: string;
  description?: string;
  painPoint?: string;
  order?: number;
}

export interface GeneratePostIdeasRequest {
  strategyId: string;
  seriesFormatId?: string;
  count?: number;
  provider?: 'openai' | 'claude' | 'perplexity';
}

export interface GeneratedPostIdeaSuggestion {
  title: string;
  topic: string;
  description: string;
  painPoint: string;
}

export const postIdeaApi = {
  create: async (data: CreatePostIdeaRequest) => {
    const response = await api.post('/post-ideas', data);
    return response.data as PostIdea;
  },

  bulkCreate: async (ideas: CreatePostIdeaRequest[]) => {
    const response = await api.post('/post-ideas/bulk', { ideas });
    return response.data as PostIdea[];
  },

  generate: async (data: GeneratePostIdeasRequest) => {
    const response = await api.post('/post-ideas/generate', data);
    return response.data.suggestions as GeneratedPostIdeaSuggestion[];
  },

  getAll: async (filters?: { seriesFormatId?: string; status?: string }) => {
    const response = await api.get('/post-ideas', {
      params: filters,
    });
    return response.data as PostIdea[];
  },

  getById: async (id: string) => {
    const response = await api.get(`/post-ideas/${id}`);
    return response.data as PostIdea;
  },

  update: async (id: string, data: Partial<CreatePostIdeaRequest> & { status?: string }) => {
    const response = await api.put(`/post-ideas/${id}`, data);
    return response.data as PostIdea;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/post-ideas/${id}`);
    return response.data;
  },
};

// Content Brief API
export interface ContentBrief {
  id: string;
  postIdeaId: string;
  theme: string;
  challenge: string;
  turningPoint?: string;
  learning: string;
  fullBrief?: string;
  createdAt: string;
  updatedAt: string;
  postIdea?: PostIdea;
}

export interface CreateContentBriefRequest {
  postIdeaId: string;
  theme: string;
  challenge: string;
  turningPoint?: string;
  learning: string;
  fullBrief?: string;
}

export interface GenerateContentBriefRequest {
  postIdeaId: string;
  provider?: 'openai' | 'claude' | 'perplexity';
}

export interface GeneratedContentBriefSuggestion {
  theme: string;
  challenge: string;
  turningPoint: string;
  learning: string;
  fullBrief: string;
}

export const contentBriefApi = {
  create: async (data: CreateContentBriefRequest) => {
    const response = await api.post('/content-briefs', data);
    return response.data as ContentBrief;
  },

  generate: async (data: GenerateContentBriefRequest) => {
    const response = await api.post('/content-briefs/generate', data);
    return response.data.suggestion as GeneratedContentBriefSuggestion;
  },

  getById: async (id: string) => {
    const response = await api.get(`/content-briefs/${id}`);
    return response.data as ContentBrief;
  },

  getByPostIdea: async (postIdeaId: string) => {
    const response = await api.get(`/content-briefs/post-idea/${postIdeaId}`);
    return response.data as ContentBrief;
  },

  update: async (id: string, data: Partial<CreateContentBriefRequest>) => {
    const response = await api.put(`/content-briefs/${id}`, data);
    return response.data as ContentBrief;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/content-briefs/${id}`);
    return response.data;
  },
};

// Editorial Plan API

/** Textversion einer Idee, wie der Wochenplan sie mitliefert (gekuerzt). */
export interface PlanLinkedInPost {
  id: string;
  postIdeaId: string;
  contentBriefId: string;
  hook: string;
  version: string;
  versionLabel?: string | null;
  isSelected: boolean;
  createdAt: string;
  publicationEntry?: {
    id: string;
    status: string;
    scheduledDate: string;
    scheduledTime?: string | null;
    linkedInPostUrl?: string | null;
  } | null;
}

export interface PlanMediaAsset {
  id: string;
  postIdeaId: string;
  kind: 'video' | 'pdf' | 'image' | 'document';
  mimeType: string;
  title?: string | null;
  originalName: string;
  profile?: 'david' | 'florian' | 'neutral' | null;
}

export interface PlanScheduledPost {
  id: string;
  postIdeaId: string;
  status: string;
  scheduledDate: string;
  linkedInPostUrl?: string | null;
}

/** Idee samt allem, was an ihr haengt (nur im Wochenplan-Detail). */
export interface PlanPostIdea extends PostIdea {
  linkedInPosts?: PlanLinkedInPost[];
  mediaAssets?: PlanMediaAsset[];
  scheduledPosts?: PlanScheduledPost[];
}

export interface EditorialPlanSlot {
  id: string;
  editorialPlanId: string;
  scheduledDate: string;
  postIdeaId?: string;
  seriesFormatId?: string;
  notes?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
  postIdea?: PlanPostIdea | null;
}

export interface EditorialPlan {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  postsPerWeek: number;
  schedule: Record<string, string>;
  postIdeaIds: string[];
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  postIdeas?: PostIdea[];
  scheduledSlots?: EditorialPlanSlot[];
}

export interface EditorialPlanSlotInput {
  scheduledDate: string;
  postIdeaId?: string;
  seriesFormatId?: string;
  notes?: string;
}

export interface CreateEditorialPlanRequest {
  name: string;
  startDate: string;
  endDate: string;
  postsPerWeek?: number;
  schedule?: Record<string, string>;
  postIdeaIds?: string[];
  slots?: EditorialPlanSlotInput[];
}

export interface AutoGenerateEditorialPlanRequest {
  name?: string;
  startDate?: string;
  postsPerWeek?: number;
  publishDays?: string[];
  publishTime?: string;
  seriesFormatId?: string;
  postIdeaIds?: string[];
}

export interface EditorialPlanPreviewSlot {
  scheduledDate: string;
  postIdeaId: string;
  seriesFormatId?: string;
  postIdea?: PostIdea;
}

export interface EditorialPlanPreview {
  name: string;
  startDate: string;
  endDate: string;
  postsPerWeek: number;
  publishDays: string[];
  publishTime: string;
  postIdeas: PostIdea[];
  slots: EditorialPlanPreviewSlot[];
}

export const editorialPlanApi = {
  create: async (data: CreateEditorialPlanRequest) => {
    const response = await api.post('/editorial-plans', data);
    return response.data as EditorialPlan;
  },

  previewAuto: async (data: AutoGenerateEditorialPlanRequest = {}) => {
    const response = await api.post('/editorial-plans/auto/preview', data);
    return response.data as EditorialPlanPreview;
  },

  autoGenerate: async (data: AutoGenerateEditorialPlanRequest = {}) => {
    const response = await api.post('/editorial-plans/auto/generate', data);
    return response.data as EditorialPlan;
  },

  getAll: async (includeInactive: boolean = false) => {
    const response = await api.get('/editorial-plans', {
      params: { includeInactive },
    });
    return response.data as EditorialPlan[];
  },

  getById: async (id: string, withPostIdeas: boolean = false) => {
    const response = await api.get(`/editorial-plans/${id}`, {
      params: { withPostIdeas },
    });
    return response.data as EditorialPlan;
  },

  update: async (id: string, data: Partial<CreateEditorialPlanRequest> & { isActive?: boolean }) => {
    const response = await api.put(`/editorial-plans/${id}`, data);
    return response.data as EditorialPlan;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/editorial-plans/${id}`);
    return response.data;
  },

  addSlot: async (planId: string, slot: EditorialPlanSlotInput) => {
    const response = await api.post(`/editorial-plans/${planId}/slots`, slot);
    return response.data as EditorialPlanSlot;
  },

  updateSlot: async (slotId: string, slot: Partial<EditorialPlanSlotInput>) => {
    const response = await api.put(`/editorial-plans/slots/${slotId}`, slot);
    return response.data as EditorialPlanSlot;
  },

  deleteSlot: async (slotId: string) => {
    const response = await api.delete(`/editorial-plans/slots/${slotId}`);
    return response.data;
  },
};

// Template API
export interface TemplateVariable {
  name: string;
  description?: string;
  example?: string;
}

export interface Template {
  id: string;
  name: string;
  description?: string;
  category: string;
  prompt: string;
  systemPrompt?: string;
  variables: TemplateVariable[];
  isPublic: boolean;
  isSystem: boolean;
  userId?: string;
  usageCount: number;
  rating?: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  category: string;
  prompt: string;
  systemPrompt?: string;
  variables?: TemplateVariable[];
  isPublic?: boolean;
  tags?: string[];
}

export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  category?: string;
  prompt?: string;
  systemPrompt?: string;
  variables?: TemplateVariable[];
  isPublic?: boolean;
  tags?: string[];
}

export interface GetTemplatesFilters {
  category?: string;
  isPublic?: boolean;
  isSystem?: boolean;
  myTemplates?: boolean;
  search?: string;
  tags?: string[];
}

export const templateApi = {
  getTemplates: async (filters?: GetTemplatesFilters) => {
    const params: any = {};
    if (filters?.category) params.category = filters.category;
    if (filters?.isPublic !== undefined) params.isPublic = filters.isPublic;
    if (filters?.isSystem !== undefined) params.isSystem = filters.isSystem;
    if (filters?.myTemplates) params.myTemplates = filters.myTemplates;
    if (filters?.search) params.search = filters.search;
    if (filters?.tags) params.tags = filters.tags;

    const response = await api.get('/templates', { params });
    return response.data as Template[];
  },

  getTemplate: async (id: string) => {
    const response = await api.get(`/templates/${id}`);
    return response.data as Template;
  },

  createTemplate: async (data: CreateTemplateRequest) => {
    const response = await api.post('/templates', data);
    return response.data as Template;
  },

  updateTemplate: async (id: string, data: UpdateTemplateRequest) => {
    const response = await api.put(`/templates/${id}`, data);
    return response.data as Template;
  },

  deleteTemplate: async (id: string) => {
    const response = await api.delete(`/templates/${id}`);
    return response.data;
  },

  useTemplate: async (id: string) => {
    const response = await api.post(`/templates/${id}/use`);
    return response.data;
  },

  getCategories: async () => {
    const response = await api.get('/templates/categories');
    return response.data as string[];
  },

  getTags: async () => {
    const response = await api.get('/templates/tags');
    return response.data as string[];
  },
};

// LinkedIn Profile API
export interface LinkedInProfile {
  id: string;
  name: string;
  username: string;
  profileUrl?: string;
  notes?: string;
  examplePosts?: string; // JSON string
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLinkedInProfileRequest {
  name: string;
  username: string;
  profileUrl?: string;
  notes?: string;
  examplePosts?: string[];
}

export const linkedInProfileApi = {
  create: async (data: CreateLinkedInProfileRequest) => {
    const response = await api.post('/linkedin-profiles', data);
    return response.data as LinkedInProfile;
  },

  getAll: async () => {
    const response = await api.get('/linkedin-profiles');
    return response.data as LinkedInProfile[];
  },

  getById: async (id: string) => {
    const response = await api.get(`/linkedin-profiles/${id}`);
    return response.data as LinkedInProfile;
  },

  update: async (id: string, data: Partial<CreateLinkedInProfileRequest>) => {
    const response = await api.put(`/linkedin-profiles/${id}`, data);
    return response.data as LinkedInProfile;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/linkedin-profiles/${id}`);
    return response.data;
  },
};

// Strategy Result API
export interface StrategyResult {
  id: string;
  name: string;
  targetAudience: string;
  industry: string;
  icp?: string;
  challenges?: string; // JSON string
  painPoints?: string; // JSON string
  trends?: string; // JSON string
  contentGaps?: string; // JSON string
  rawAnalysis?: string;
  linkedInProfileIds?: string; // JSON string
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStrategyResultRequest {
  name: string;
  targetAudience: string;
  industry: string;
  icp?: string;
  challenges?: string[];
  painPoints?: string[];
  trends?: string[];
  contentGaps?: string[];
  rawAnalysis?: string;
  linkedInProfileIds?: string[];
}

export const strategyApi = {
  create: async (data: CreateStrategyResultRequest) => {
    const response = await api.post('/strategy', data);
    return response.data as StrategyResult;
  },

  getAll: async (includeInactive: boolean = false) => {
    const response = await api.get('/strategy', {
      params: { includeInactive },
    });
    return response.data as StrategyResult[];
  },

  getById: async (id: string) => {
    const response = await api.get(`/strategy/${id}`);
    return response.data as StrategyResult;
  },

  update: async (id: string, data: Partial<CreateStrategyResultRequest> & { isActive?: boolean }) => {
    const response = await api.put(`/strategy/${id}`, data);
    return response.data as StrategyResult;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/strategy/${id}`);
    return response.data;
  },
};

// Lead Magnet API
export interface LeadMagnet {
  id: string;
  strategyId?: string;
  name: string;
  type: string; // "checklist", "guide", "template", "ebook", "worksheet", "cheatsheet"
  description?: string;
  targetAudience?: string;
  content: string;
  format: string;
  provider: string;
  model?: string;
  userId: string;
  isActive: boolean;
  hostedUrl?: string;
  createdAt: string;
  updatedAt: string;
  strategy?: StrategyResult;
  /** ICP-Kampagnen, die den Lead-Magnet als CTA nutzen (nur in der Liste). */
  verwendetIn?: { id: string; name: string }[];
}

export interface CreateLeadMagnetRequest {
  name: string;
  type: string;
  description?: string;
  targetAudience?: string;
  strategyId?: string;
  hostedUrl?: string;
}

export interface GenerateLeadMagnetContentRequest {
  type: string;
  strategyId?: string;
  customPrompt?: string;
}

export interface UpdateLeadMagnetRequest {
  name?: string;
  type?: string;
  description?: string;
  targetAudience?: string;
  content?: string;
  isActive?: boolean;
  hostedUrl?: string;
}

export const leadMagnetApi = {
  getAll: async (includeInactive: boolean = false) => {
    const response = await api.get('/lead-magnets', {
      params: { includeInactive },
    });
    return response.data as LeadMagnet[];
  },

  getById: async (id: string) => {
    const response = await api.get(`/lead-magnets/${id}`);
    return response.data as LeadMagnet;
  },

  create: async (data: CreateLeadMagnetRequest) => {
    const response = await api.post('/lead-magnets', data);
    return response.data as LeadMagnet;
  },

  generateContent: async (data: GenerateLeadMagnetContentRequest) => {
    const response = await api.post('/lead-magnets/generate', data);
    return response.data as { content: string; provider: string; model: string };
  },

  update: async (id: string, data: UpdateLeadMagnetRequest) => {
    const response = await api.put(`/lead-magnets/${id}`, data);
    return response.data as LeadMagnet;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/lead-magnets/${id}`);
    return response.data;
  },
};

// LinkedIn Integration API
export interface LinkedInConnectionInfo {
  id: string;
  linkedInUserId: string;
  displayName: string | null;
  profilePicUrl: string | null;
  isActive: boolean;
  tokenExpiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface LinkedInStatus {
  configured: boolean;
  connections: LinkedInConnectionInfo[];
  /** Testmodus (LINKEDIN_DRY_RUN, sonst OUTREACH_DRY_RUN): es wird nichts gepostet. */
  dryRun?: boolean;
}

export interface PostMetrics {
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
  engagement: number;
  fetchedAt: string;
}

export interface PostStatsSummary {
  postId: string;
  content: string;
  scheduledDate: string;
  postedAt: string | null;
  linkedInPostUrl: string | null;
  postIdea?: { id: string; title: string; topic: string } | null;
  latestMetrics: PostMetrics | null;
}

export interface StatisticsOverview {
  totalPosts: number;
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalImpressions: number;
  averageEngagement: number;
  bestPerformingPost: PostStatsSummary | null;
  postsPerWeek: { week: string; count: number }[];
  engagementTrend: { date: string; likes: number; comments: number; shares: number }[];
}

export interface ScheduledPostsOverview {
  pending: number;
  approved: number;
  rejected: number;
  posted: number;
  upcoming: Array<{
    id: string;
    scheduledDate: string;
    status: string;
    contentPreview: string;
    postIdea?: { id: string; title: string; topic: string } | null;
  }>;
}

export const linkedInApi = {
  getStatus: async () => {
    const response = await api.get('/linkedin/status');
    return response.data as LinkedInStatus;
  },

  initiateOAuth: async () => {
    const response = await api.get('/linkedin/auth');
    return response.data as { authUrl: string };
  },

  disconnect: async (connectionId: string) => {
    const response = await api.delete(`/linkedin/connections/${connectionId}`);
    return response.data;
  },

  publishPost: async (scheduledPostId: string) => {
    const response = await api.post(`/linkedin/publish/${scheduledPostId}`);
    return response.data as ScheduledPost;
  },

  syncMetrics: async () => {
    const response = await api.post('/linkedin/sync-metrics');
    return response.data;
  },

  getStatistics: async (months: number = 3) => {
    const response = await api.get('/linkedin/statistics', {
      params: { months },
    });
    return response.data as StatisticsOverview;
  },

  getPostsWithMetrics: async (options?: { months?: number; limit?: number; offset?: number }) => {
    const response = await api.get('/linkedin/statistics/posts', {
      params: options,
    });
    return response.data as { posts: PostStatsSummary[]; total: number; limit: number; offset: number };
  },

  getScheduledOverview: async () => {
    const response = await api.get('/linkedin/statistics/scheduled');
    return response.data as ScheduledPostsOverview;
  },

  getPostMetricsHistory: async (scheduledPostId: string) => {
    const response = await api.get(`/linkedin/statistics/posts/${scheduledPostId}/history`);
    return response.data as PostMetrics[];
  },

  updatePostContent: async (scheduledPostId: string, content: string) => {
    const response = await api.put(`/scheduled-posts/${scheduledPostId}/content`, { content });
    return response.data as ScheduledPost;
  },
};

// Meme API
export interface MemeTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  popularityScore?: number;
  imageUrl?: string;
  imgflipId?: string;
  width?: number;
  height?: number;
  boxCount?: number;
}

export interface MemeTextResult {
  topText: string;
  bottomText: string;
  template: MemeTemplate;
  explanation: string;
  generatedImageUrl?: string;
}

export const memeApi = {
  getTemplates: async () => {
    const response = await api.get('/memes/templates');
    return response.data as { success: boolean; templates: MemeTemplate[] };
  },

  generateText: async (data: {
    topic: string;
    targetAudience?: string;
    tone?: string;
    templateId?: string;
  }) => {
    const response = await api.post('/memes/generate-text', data);
    return response.data as { success: boolean; result: MemeTextResult; error?: string };
  },

  generateImage: async (data: {
    templateId: string;
    imgflipId?: string;
    topText: string;
    bottomText: string;
  }) => {
    const response = await api.post('/memes/generate-image', data);
    return response.data as { success: boolean; result: MemeTextResult; error?: string };
  },
};

// LinkedIn Post Generator API
export interface LinkedInPost {
  id: string;
  contentBriefId: string;
  postIdeaId: string;
  seriesFormatId?: string;
  hook: string;
  content: string;
  cta: string;
  footer: string;
  fullPost: string;
  version: string;
  versionLabel?: string;
  isSelected: boolean;
  provider: string;
  model?: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  publicationEntry?: PublicationCalendarEntry;
}

export interface GenerateLinkedInPostRequest {
  contentBriefId: string;
  provider?: 'openai' | 'claude' | 'perplexity';
  variants?: number;
}

export interface GeneratedLinkedInPostSuggestion {
  hook: string;
  content: string;
  cta: string;
  footer: string;
  fullPost: string;
  versionLabel: string;
}

export interface SaveLinkedInPostsRequest {
  contentBriefId: string;
  postIdeaId: string;
  seriesFormatId?: string;
  posts: GeneratedLinkedInPostSuggestion[];
  provider?: string;
}

export const linkedInPostApi = {
  generate: async (data: GenerateLinkedInPostRequest) => {
    const response = await api.post('/linkedin-posts/generate', data);
    return response.data.suggestions as GeneratedLinkedInPostSuggestion[];
  },

  save: async (data: SaveLinkedInPostsRequest) => {
    const response = await api.post('/linkedin-posts/save', data);
    return response.data as LinkedInPost[];
  },

  getAll: async () => {
    const response = await api.get('/linkedin-posts');
    return response.data as LinkedInPost[];
  },

  getByContentBrief: async (contentBriefId: string) => {
    const response = await api.get(`/linkedin-posts/brief/${contentBriefId}`);
    return response.data as LinkedInPost[];
  },

  getById: async (id: string) => {
    const response = await api.get(`/linkedin-posts/${id}`);
    return response.data as LinkedInPost;
  },

  update: async (id: string, data: Partial<{ hook: string; content: string; cta: string; footer: string; fullPost: string; versionLabel: string; isSelected: boolean }>) => {
    const response = await api.put(`/linkedin-posts/${id}`, data);
    return response.data as LinkedInPost;
  },

  selectVersion: async (id: string) => {
    const response = await api.post(`/linkedin-posts/${id}/select`);
    return response.data as LinkedInPost;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/linkedin-posts/${id}`);
    return response.data;
  },

  deleteByContentBrief: async (contentBriefId: string) => {
    const response = await api.delete(`/linkedin-posts/brief/${contentBriefId}`);
    return response.data;
  },
};

// Publication Calendar API
export interface PublicationCalendarEntry {
  id: string;
  linkedInPostId: string;
  scheduledDate: string;
  scheduledTime?: string;
  status: string;
  linkedInPostUrl?: string;
  linkedInNativeId?: string;
  publishError?: string;
  notes?: string;
  userId: string;
  /** Gewaehltes LinkedIn-Profil (Veroeffentlichungsprofil); leer = eigenes Profil beim Veroeffentlichen. */
  linkedInConnectionId?: string | null;
  /** Wer ueber Intermal veroeffentlicht hat und wann. */
  publishedByUserId?: string | null;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  linkedInPost?: LinkedInPost;
}

/** Mediendatei, die mit einem Kalender-Eintrag auf LinkedIn gehen kann. */
export interface LinkedInMedienKandidat {
  id: string;
  title: string;
  kind: 'image' | 'video' | 'pdf' | string;
  sizeBytes: number;
}

/**
 * Antwort von POST /publication-calendar/:id/publish-linkedin.
 * simulated: Testmodus, nichts gepostet. laeuft: LinkedIn verarbeitet noch
 * (HTTP 202), der Eintrag steht auf `publishing`.
 */
export interface LinkedInVeroeffentlichung {
  simulated: boolean;
  laeuft?: boolean;
  entry: PublicationCalendarEntry;
  medium: LinkedInMedienKandidat | null;
  linkedInPostUrl?: string | null;
  zeichen?: number;
  verbunden?: boolean;
  hinweis?: string;
}

export interface CreatePublicationEntryRequest {
  linkedInPostId: string;
  scheduledDate: string;
  scheduledTime?: string;
  notes?: string;
}

export const publicationCalendarApi = {
  create: async (data: CreatePublicationEntryRequest) => {
    const response = await api.post('/publication-calendar', data);
    return response.data as PublicationCalendarEntry;
  },

  getAll: async (filters?: { status?: string; startDate?: string; endDate?: string }) => {
    const response = await api.get('/publication-calendar', { params: filters });
    return response.data as PublicationCalendarEntry[];
  },

  getById: async (id: string) => {
    const response = await api.get(`/publication-calendar/${id}`);
    return response.data as PublicationCalendarEntry;
  },

  update: async (id: string, data: Partial<{ scheduledDate: string; scheduledTime: string; status: string; notes: string; linkedInConnectionId: string | null }>) => {
    const response = await api.put(`/publication-calendar/${id}`, data);
    return response.data as PublicationCalendarEntry;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/publication-calendar/${id}`);
    return response.data;
  },

  approve: async (id: string) => {
    const response = await api.post(`/publication-calendar/${id}/approve`);
    return response.data as PublicationCalendarEntry;
  },

  schedule: async (id: string) => {
    const response = await api.post(`/publication-calendar/${id}/schedule`);
    return response.data as PublicationCalendarEntry;
  },

  markPublished: async (id: string, linkedInPostUrl?: string, linkedInNativeId?: string) => {
    const response = await api.post(`/publication-calendar/${id}/publish`, {
      linkedInPostUrl,
      linkedInNativeId,
    });
    return response.data as PublicationCalendarEntry;
  },

  /**
   * Postet den Eintrag ueber das eigene LinkedIn-Konto. Haengen mehrere
   * Medien an der Idee, antwortet das Backend mit 400 und `kandidaten`.
   */
  publishToLinkedIn: async (id: string, mediaAssetId?: string) => {
    const response = await api.post(`/publication-calendar/${id}/publish-linkedin`, mediaAssetId ? { mediaAssetId } : {});
    return response.data as LinkedInVeroeffentlichung;
  },
};

// Instagram, X und Facebook (Backend: /api/social, /api/social-posts, /api/social-publications)
export type SocialPlattform = 'instagram' | 'x' | 'facebook';
export type SocialKontoArt = 'mandant' | 'person';

export interface SocialConnectionInfo {
  id: string;
  platform: SocialPlattform;
  art: SocialKontoArt;
  userId: string;
  accountId: string;
  accountName: string | null;
  isActive: boolean;
  tokenExpiresAt: string | null;
  createdAt: string;
}

export interface SocialStatus {
  plattformen: Record<SocialPlattform, { configured: boolean; dryRun: boolean; redirectUri?: string; nurSeiten?: boolean }>;
  connections: SocialConnectionInfo[];
  verbrauch: {
    x: { monat: string; kostenCents: number; beitraege: number; preise: { post: number; link: number } };
    instagram: { letzte24h: number; limit: number };
  };
  darfFirmenkonto: boolean;
}

export interface SocialPost {
  id: string;
  postIdeaId: string;
  contentBriefId?: string | null;
  platform: SocialPlattform;
  version: string;
  versionLabel?: string | null;
  text: string;
  hashtags?: string | null;
  thread?: string | null;
  isSelected: boolean;
  provider: string;
  createdAt: string;
  publications?: Array<{ id: string; status: string; scheduledDate: string; url?: string | null }>;
}

export interface SocialVorschlag {
  platform: SocialPlattform;
  versionLabel: string;
  text: string;
  hashtags?: string;
  tweets?: string[];
  zeichen: number | number[];
  hinweise: string[];
}

export interface SocialKosten {
  cents: number;
  posts: number;
  linkPosts: number;
}

export interface SocialPublication {
  id: string;
  socialPostId: string;
  platform: SocialPlattform;
  connectionId?: string | null;
  scheduledDate: string;
  scheduledTime?: string | null;
  status: string;
  nativeId?: string | null;
  url?: string | null;
  publishError?: string | null;
  publishedAt?: string | null;
  mediaAssetId?: string | null;
  kostenCents?: number | null;
  notes?: string | null;
  userId: string;
  socialPost?: SocialPost;
  connection?: SocialConnectionInfo | null;
}

/** Antwort von POST /social-publications/:id/publish (wie LinkedInVeroeffentlichung). */
export interface SocialVeroeffentlichung {
  simulated: boolean;
  laeuft?: boolean;
  entry: SocialPublication;
  medium: LinkedInMedienKandidat | null;
  kosten?: SocialKosten | null;
  url?: string | null;
  zeichen?: number | number[];
  tweets?: number;
  verbunden?: boolean;
  hinweis?: string;
}

/** Plattformen der Veroeffentlichungsprofile (LinkedIn plus Instagram, X, Facebook). */
export type ProfilPlattform = 'linkedin' | SocialPlattform;
export type ProfilStatus = 'aktiv' | 'laeuftAb' | 'abgelaufen' | 'getrennt';

/** Ein Konto, ueber das veroeffentlicht werden kann (Backend: GET /social/profile; nie Tokens). */
export interface Veroeffentlichungsprofil {
  id: string;
  plattform: ProfilPlattform;
  art: SocialKontoArt;
  kontoName: string;
  person: { id: string; name: string } | null;
  eigenes: boolean;
  anzeigeName: string;
  status: ProfilStatus;
  tokenExpiresAt: string | null;
  verbundenSeit: string;
}

export const socialApi = {
  getStatus: async () => {
    const response = await api.get('/social/status');
    return response.data as SocialStatus;
  },
  /** Veroeffentlichungsprofile des Mandanten (linkedin.lesen); fremde persoenliche nutzt nur, wer freigeben darf. */
  getProfile: async (platform?: ProfilPlattform) => {
    const response = await api.get('/social/profile', { params: platform ? { platform } : undefined });
    return response.data as { profile: Veroeffentlichungsprofil[]; darfTeamprofile: boolean };
  },
  initiateOAuth: async (platform: SocialPlattform, art: SocialKontoArt) => {
    const response = await api.get(`/social/${platform}/auth`, { params: { art } });
    return response.data as { authUrl: string };
  },
  disconnect: async (connectionId: string) => {
    const response = await api.delete(`/social/connections/${connectionId}`);
    return response.data;
  },
  facebookSeiten: async (auswahlId: string) => {
    const response = await api.get(`/social/facebook/seiten/${encodeURIComponent(auswahlId)}`);
    return response.data as { seiten: Array<{ id: string; name: string }> };
  },
  facebookSeitenUebernehmen: async (auswahlId: string, seitenIds: string[]) => {
    const response = await api.post(`/social/facebook/seiten/${encodeURIComponent(auswahlId)}`, { seitenIds });
    return response.data;
  },
};

export const socialPostApi = {
  generate: async (data: { postIdeaId: string; contentBriefId?: string; platform: SocialPlattform; variants?: number; provider?: string }) => {
    const response = await api.post('/social-posts/generate', data);
    return response.data as { suggestions: SocialVorschlag[]; postIdeaId: string; contentBriefId: string | null };
  },
  save: async (data: { postIdeaId: string; contentBriefId?: string; platform: SocialPlattform; posts: Array<{ text?: string; hashtags?: string; tweets?: string[]; versionLabel?: string }>; provider?: string; ersetzen?: boolean }) => {
    const response = await api.post('/social-posts/save', data);
    return response.data as SocialPost[];
  },
  getByIdea: async (postIdeaId: string, platform?: SocialPlattform) => {
    const response = await api.get(`/social-posts/idea/${encodeURIComponent(postIdeaId)}`, { params: platform ? { platform } : undefined });
    return response.data as SocialPost[];
  },
  update: async (id: string, data: { text?: string; hashtags?: string | null; tweets?: string[]; versionLabel?: string }) => {
    const response = await api.put(`/social-posts/${id}`, data);
    return response.data as SocialPost & { pruefung?: { fehler: string[]; hinweise: string[] } };
  },
  select: async (id: string) => {
    const response = await api.post(`/social-posts/${id}/select`);
    return response.data as SocialPost;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/social-posts/${id}`);
    return response.data;
  },
  /** Je Idee die Plattformen mit Fassungen (hoechstens 200 Ids je Abfrage). */
  vorhanden: async (postIdeaIds: string[]) => {
    const response = await api.get('/social-posts/vorhanden', { params: { postIdeaIds: postIdeaIds.join(',') } });
    return response.data as Record<string, SocialPlattform[]>;
  },
};

/** Durchlauf "Fassungen fuer viele Ideen" (Backend: social-stapel.service.ts). */
export type SocialStapelStatus = 'laufend' | 'fertig' | 'abgebrochen' | 'kontofehler';

export interface SocialStapelJob {
  id: string;
  editorialPlanId: string | null;
  startedAt: string;
  endedAt: string | null;
  platforms: SocialPlattform[];
  variants: number;
  nurFehlende: boolean;
  ideen: number;
  total: number;
  done: number;
  erzeugt: number;
  skipped: number;
  failed: Array<{ postIdeaId: string; titel: string; platform: SocialPlattform; meldung: string }>;
  status: SocialStapelStatus;
  aktuell: { postIdeaId: string; titel: string; platform: SocialPlattform } | null;
  meldung: string | null;
  abbruchAngefordert: boolean;
}

/**
 * Stand der automatischen Fassungen im Mandanten (nach dem Speichern von
 * LinkedIn-Varianten und einmal nach jedem Start; Backend: social-stapel.service.ts).
 */
export interface SocialAutoStand {
  laufend: boolean;
  offen: number;
  gesamt: number;
  fertig: number;
  fehler: number;
  letzterFehler: string | null;
  kontofehler: string | null;
  verworfen: number;
  aktuell: { postIdeaId: string; titel: string; platform: SocialPlattform } | null;
  startedAt: string | null;
  endedAt: string | null;
  /** Kombinationen (Idee x Plattform), die automatisch mindestens dreimal scheiterten und nicht mehr nachgeholt werden. */
  dauerhaftFehlgeschlagen?: number;
  dauerhaft?: Array<{ postIdeaId: string; titel: string; platform: SocialPlattform; versuche: number; letzterFehler: string | null }>;
}

export const socialStapelApi = {
  /** 202 { jobId, total, job }; 409 { error, jobId }, wenn im Mandanten schon einer laeuft. */
  starten: async (data: { editorialPlanId?: string; postIdeaIds?: string[]; platforms: SocialPlattform[]; variants: number; nurFehlende: boolean }) => {
    const response = await api.post('/social-posts/stapel', data);
    return response.data as { jobId: string; total: number; job: SocialStapelJob };
  },
  stand: async (jobId: string) => {
    const response = await api.get(`/social-posts/stapel/${encodeURIComponent(jobId)}`);
    return response.data as SocialStapelJob;
  },
  /** Laufender oder letzter Durchlauf im Mandanten. */
  letzter: async () => {
    const response = await api.get('/social-posts/stapel');
    return (response.data as { job: SocialStapelJob | null }).job;
  },
  /** Laufender oder letzter Durchlauf und Stand der automatischen Fassungen. */
  zustand: async () => {
    const response = await api.get('/social-posts/stapel');
    const daten = (response.data || {}) as { job?: SocialStapelJob | null; auto?: SocialAutoStand | null };
    return { job: daten.job ?? null, auto: daten.auto ?? null };
  },
  abbrechen: async (jobId: string) => {
    const response = await api.post(`/social-posts/stapel/${encodeURIComponent(jobId)}/abbrechen`);
    return response.data as SocialStapelJob;
  },
};

export const socialPublicationApi = {
  create: async (data: { socialPostId: string; scheduledDate: string; scheduledTime?: string; connectionId?: string | null; mediaAssetId?: string | null; notes?: string }) => {
    const response = await api.post('/social-publications', data);
    return response.data as SocialPublication;
  },
  getAll: async (filters?: { platform?: SocialPlattform; status?: string }) => {
    const response = await api.get('/social-publications', { params: filters });
    return response.data as SocialPublication[];
  },
  update: async (id: string, data: { scheduledDate?: string; scheduledTime?: string; notes?: string; connectionId?: string | null; mediaAssetId?: string | null }) => {
    const response = await api.put(`/social-publications/${id}`, data);
    return response.data as SocialPublication;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/social-publications/${id}`);
    return response.data;
  },
  approve: async (id: string) => {
    const response = await api.post(`/social-publications/${id}/approve`);
    return response.data as SocialPublication;
  },
  publish: async (id: string, data: { mediaAssetId?: string; connectionId?: string; kostenBestaetigt?: boolean } = {}) => {
    const response = await api.post(`/social-publications/${id}/publish`, data);
    return response.data as SocialVeroeffentlichung;
  },
  markPublished: async (id: string, url?: string) => {
    const response = await api.post(`/social-publications/${id}/mark-published`, url ? { url } : {});
    return response.data as SocialPublication;
  },
};

// "Mein Profil verbinden" (Backend: /api/mein-profil, jede angemeldete Person)
export type MeinProfilPlattform = 'linkedin' | 'x' | 'instagram';

export interface MeinKonto {
  id: string;
  kontoName: string;
  verbundenSeit: string;
  tokenExpiresAt: string | null;
  status: ProfilStatus;
}

export interface MeinProfilUebersicht {
  plattformen: Record<MeinProfilPlattform, { configured: boolean; konten: MeinKonto[] }>;
  veroeffentlichungen: Array<{
    plattform: MeinProfilPlattform;
    datum: string;
    url: string | null;
    kontoName: string;
    durch: { id: string; name: string; selbst: boolean } | null;
  }>;
}

export const meinProfilApi = {
  get: async () => {
    const response = await api.get('/mein-profil');
    return response.data as MeinProfilUebersicht;
  },
  initiateOAuth: async (platform: MeinProfilPlattform) => {
    const response = await api.get(`/mein-profil/${platform}/auth`);
    return response.data as { authUrl: string };
  },
  disconnect: async (platform: MeinProfilPlattform) => {
    const response = await api.delete(`/mein-profil/${platform}`);
    return response.data as { message: string; getrennt: number };
  },
};

export default api;
